import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount, useMsal } from '@azure/msal-react';
import {
  AssetConflictChoice,
  AssetSaveResult,
  AssetUpsertRequest,
  AssetVersionedView,
  getAssetFieldConflicts,
  isAssetConcurrencyConflict,
  resolveAssetConflicts,
} from '@trackly/shared';
import { useAppSelector } from '../../../app/hooks';
import { useUpsertAssetMutation } from '../../../services/AssetService/assetApi';
import type { AssetConflictState } from '../components/AssetForm/actions/AssetConflictDialog';

export interface SaveNotice {
  severity: 'success' | 'error';
  message: string;
  // An error the user has to act on stays until dismissed instead of timing out.
  persistent: boolean;
}

type PendingConflict = AssetConflictState & { request: AssetUpsertRequest };

const MERGED_MESSAGE = 'Asset saved. Your changes were merged with a newer change made by someone else.';
const CONFLICT_MESSAGE =
  'This asset was changed by someone else while you were editing it. Save again to merge your changes, or reload the page to see the latest values.';
const RESOLUTION_CONFLICT_MESSAGE =
  'This asset was changed by someone else again. Choose Save Resolution to try again, or Cancel to review the latest values.';
const GENERIC_FAILURE_MESSAGE = 'Failed to save asset. Please try again.';

function withoutVersion({ etag: _etag, ...view }: AssetVersionedView) {
  return view;
}

// The version and the baseline travel beside the asset, never inside it. A new asset has neither
// to check against, so its request stays exactly what it was before versions existed.
function buildUpsertRequest(
  asset: AssetVersionedView,
  base: AssetVersionedView | null,
  modifiedBy: string
): AssetUpsertRequest {
  const assetView = withoutVersion(asset);
  if (!asset.id || !asset.etag) return { assetView, modifiedBy };

  return { assetView, modifiedBy, expectedEtag: asset.etag, ...(base ? { base: withoutVersion(base) } : {}) };
}

function pendingConflict(request: AssetUpsertRequest, error: unknown): PendingConflict | null {
  const conflicts = getAssetFieldConflicts(error);
  if (!conflicts) return null;

  const choices = Object.fromEntries(conflicts.map(c => [c.field, 'mine' as AssetConflictChoice]));
  return { request, conflicts, choices };
}

function failureNotice(error: unknown, fromDialog: boolean): SaveNotice {
  const isConflict = isAssetConcurrencyConflict(error);
  const conflictMessage = fromDialog ? RESOLUTION_CONFLICT_MESSAGE : CONFLICT_MESSAGE;
  return {
    severity: 'error',
    message: isConflict ? conflictMessage : GENERIC_FAILURE_MESSAGE,
    persistent: isConflict,
  };
}

/**
 * The asset save and overlapping-edit resolution flow (state, upsert call, 409 handling), so
 * `SaveAssetButton` stays focused on validation and rendering. Mirrors `useSaveTicket` (spec 010).
 * The slice refreshes the draft and the baseline on a successful upsert; on every failure the draft
 * is left exactly as the user had it.
 */
export function useSaveAsset(successMessage: string) {
  const { accounts } = useMsal();
  const modifiedBy = useAccount(accounts[0] || {})?.name || 'Unknown User';
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState<SaveNotice | null>(null);
  const [conflict, setConflict] = useState<PendingConflict | null>(null);
  const { currentAsset, baseAsset } = useAppSelector(state => state.asset);
  const [upsertAsset] = useUpsertAssetMutation();

  // A failed resolution keeps the dialog and the user's choices, so a retry resends the same
  // resolved request; the modal dialog means nothing can change the draft in between.
  const handleFailure = (request: AssetUpsertRequest, error: unknown, fromDialog: boolean) => {
    const next = pendingConflict(request, error);
    if (next) {
      setConflict(next);
      return;
    }
    if (!fromDialog) setConflict(null);
    setNotice(failureNotice(error, fromDialog));
  };

  const submit = async (
    request: AssetUpsertRequest,
    { fromDialog = false } = {}
  ): Promise<AssetSaveResult | undefined> => {
    setIsSaving(true);
    setNotice(null);
    try {
      const result = await upsertAsset(request).unwrap();
      setConflict(null);
      setNotice({ severity: 'success', message: result.merged ? MERGED_MESSAGE : successMessage, persistent: false });
      return result;
    } catch (error) {
      console.error('Failed to save asset:', error);
      handleFailure(request, error, fromDialog);
      return undefined;
    } finally {
      setIsSaving(false);
    }
  };

  // A brand-new asset moves to its edit page once saved, so the next save carries a version.
  const save = async () => {
    if (!currentAsset) return;
    const result = await submit(buildUpsertRequest(currentAsset, baseAsset, modifiedBy));
    if (!currentAsset.id && result?.id) {
      setTimeout(() => navigate(`/assets/edit?id=${result.id}`, { replace: true }), 1500);
    }
  };

  const confirmConflict = async () => {
    if (!conflict) return;
    await submit(resolveAssetConflicts(conflict.request, conflict.conflicts, conflict.choices), { fromDialog: true });
  };

  const setChoice = (field: string, choice: AssetConflictChoice) =>
    setConflict(prev => (prev ? { ...prev, choices: { ...prev.choices, [field]: choice } } : prev));

  return {
    isSaving,
    notice,
    clearNotice: () => setNotice(null),
    conflict,
    save,
    setChoice,
    confirmConflict,
    cancelConflict: () => setConflict(null),
  };
}
